<template>
  <!-- Table Container Card -->
  <b-card no-body>
    <div class="m-2">
      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
        </b-col>
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <b-button
              variant="primary"
              class="mr-1"
              @click="showModal('modal-filter')"
            >
              <feather-icon icon="FilterIcon" />{{ $t('Filter') }}
            </b-button>
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      :items="systemAccessList"
      responsive
      :busy="isBusy"
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No data available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(incognito)="data">
        {{ transformBoolean(data.value) }}
      </template>
      <template #cell(dateCreated)="data">
        <p class="mb-0">
          {{ data.value ? formatToDateWithDateTime(data.value ) : '' }}
        </p>
      </template>
      <template #cell(dateApproved)="data">
        <p class="mb-0">
          {{ data.value ? formatToDateWithDateTime(data.value ) : '' }}
        </p>
      </template>
      <template #cell(dateLastModified)="data">
        <p class="mb-0">
          {{ data.value ? formatToDateWithDateTime(data.value ) : '' }}
        </p>
      </template>
      <template #cell(status)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission(data.item.status === 'Active' ? 'DEACTIVATE_SYSTEM_ACCESS' : 'ACTIVATE_SYSTEM_ACCESS')"
              @click="updateSystemAccessStatus(data.item, $event.target)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{ data.item.status === 'Active' ? $t('Deactivate') : $t('Activate') }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>
    </b-table>
    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="modal-filter"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      no-close-on-backdrop
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="getSystemAccess"
      @cancel="resetFilters"
    >
      <b-row class="match-height">

        <b-col md="6">
          <SelectField
            id="user-id"
            v-model="filter.userId"
            :label="$t('Requester ID')"
            :placeholder="$t('Select')"
            :options="allUsersOptions"
            item="label"
            :reduce="option => option.value"
          />
        </b-col>

        <b-col md="6">
          <SelectField
            id="approver-id"
            v-model="filter.approverID"
            :label="$t('Approver ID')"
            :placeholder="$t('Select')"
            :options="allUsersOptions"
            item="label"
            :reduce="option => option.value"
          />
        </b-col>
        <!-- Branch -->
        <b-col md="6">
          <SelectField
            id="branch"
            v-model="filter.branchCode"
            :reduce="option => option.value"
            :label="$t('Branch')"
            :placeholder="$t('Select')"
            :options="branchOptions"
            item="label"
          />
        </b-col>
        <!-- Branch -->
        <!-- Status -->
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.status"
            :label="$t('Status')"
            :placeholder="$t('Select')"
            :options="systemAccessStatusOptions"
            item="label"
            :reduce="option => option"
          />
        </b-col>
        <!-- Status -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->
    <FullScreenSpinner :show="showSpinner" />
  </b-card>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BTable,
  BDropdown,
  BDropdownItem,
  BModal,
  BBadge,
  BSpinner,
} from 'bootstrap-vue'
import { mapActions } from 'vuex'
import { required } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import { toDateWithDateTime } from '@/helpers/formatDate'
import { transformBoolean } from '../../../helpers/transformers'
import getStatusColor from '../../../helpers/getStatusColor'
import SelectField from '../../forms/SelectField.vue'
import Button from '../../components/button/Button.vue'
import { adminPermissions } from '../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    Button,
    BTable,
    BSpinner,
    BDropdown,
    BDropdownItem,
    BModal,
    BBadge,
    TableEntries,
    SelectField,
    TablePagination,
    FullScreenSpinner,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      required,
      transformBoolean,
      getStatusColor,
      formatToDateWithDateTime: toDateWithDateTime,
      sortBy: 'id',
      isBusy: false,
      sortDesc: false,
      showSpinner: false,
      tableHeaders: [
        { key: 'SN', sortable: true, label: 'S/N' },
        { key: 'dateCreated', sortable: true, label: 'Date Requested' },
        { key: 'username', sortable: true, label: 'Requester Email' },
        { key: 'user', sortable: true, label: 'Requester Full Name' },
        { key: 'userBranchName', sortable: true, label: 'Requester Branch' },
        { key: 'approverEmail', sortable: true, label: 'Approver Email' },
        { key: 'approver', sortable: true, label: 'Approver Full Name' },
        { key: 'browser', sortable: true, label: 'Browser' },
        { key: 'systemAddress', sortable: true, label: 'System Address' },
        { key: 'incognito', sortable: true, label: 'Incoginito' },
        { key: 'operatingSystem', sortable: true, label: 'Operating System' },
        { key: 'dateApproved', sortable: true, label: 'Date Approved' },
        { key: 'dateLastModified', sortable: true, label: 'Last Modified Date' },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'actions' },
      ],
      systemAccessStatusOptions: ['Active', 'Inactive'],
      allUsersOptions: [],
      systemAccessList: [],
      branchOptions: [],
      loading: false,
      modalType: {
        type: '',
        text: '',
      },
      filter: {
        userId: '',
        approverID: '',
        branchCode: '',
        status: '',
      },
      isReadonly: false,
    }
  },
  computed: {
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.getSystemAccess()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.getSystemAccess()
    },
  },
  mounted() {
    this.getSystemAccess()
    this.fetchAllUsersOptions()
    this.fetchBranches()
  },
  methods: {
    ...mapActions({
      getUserSystemAccess: 'admin/getUserSystemAccess',
      updateSystemStatus: 'admin/updateSystemStatus',
      getUsers: 'operations/getAllUsers',
      getBranch: 'operations/getBranchList',
    }),

    async updateSystemAccessStatus(items) {
      const actionType = items.status === 'Active' ? 'Inactive' : 'Active'
      const action = items.status === 'Active' ? this.$t('Deactivate') : this.$t('Activate')
      const permission = items.status === 'Active' ? 'DEACTIVATE_SYSTEM_ACCESS' : 'ACTIVATE_SYSTEM_ACCESS'

      try {
        const updateStatus = async () => {
          this.showSpinner = true
          const statusValues = items
          const response = await this.updateSystemStatus({ statusValues, actionType, permission })
          if (response.status === 200) {
            this.getSystemAccess()
            this.showSpinner = false
            const requestToText = this.$t('Your request to')
            const text = this.$t('User System Access was successful')
            this.alertSuccess({ text: `${requestToText} ${action} ${text}` })
            this.showSpinner = false
          }
        }
        const areYouSure = this.$t('Are you sure you want to')
        const saText = this.$t('this User System Access')
        this.confirm(updateStatus, { text: `${areYouSure} ${action} ${saText}?` })
      } catch (error) {
        const requestText = this.$t('Your request to')
        const reasonText = this.$t('User System Access failed. Reason')
        this.alertError({ text: `${requestText} ${action} ${reasonText}: ${error}` })
      } finally {
        this.showSpinner = false
      }
    },
    async getSystemAccess() {
      this.isBusy = true
      try {
        const response = await this.getUserSystemAccess({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: adminPermissions.VIEW_SYSTEM_ACCESS,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.systemAccessList = data.data.map(item => ({
            ...item,
            browser: `${item.browserName} ${item.browserVersion}`,
            systemAddress: `${item.ipLocation} (${item.ipAddress})`,
            operatingSystem: `${item.os} ${item.osVersion}`,
          }))
          this.totalCount = data.totalItems
          this.isBusy = false
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async fetchAllUsersOptions() {
      try {
        const formData = {
          status: 'Active',
        }
        const response = await this.getUsers({
          formData,
          permission: adminPermissions.VIEW_SYSTEM_ACCESS,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.allUsersOptions = data.data
            .map(item => ({ label: `${item?.firstname ?? ''} ${item?.lastname ?? ''} ${item?.othernames ?? ''}`, value: item.id }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async fetchBranches() {
      try {
        const response = await this.getBranch({
          search: '',
          page: 0,
          limit: 0,
          permission: adminPermissions.VIEW_SYSTEM_ACCESS,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.branchOptions = data.data
            .filter(dataItem => dataItem.status === 'Active')
            .map(item => ({
              label: item.name,
              value: item.code.toString(),
            }))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.getSystemAccess()
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
